/**
 * 一次「顾客会话」的标识。
 *
 * 两个标识，作用完全不同，别混：
 *
 *   customerOpenId —— 顾客身份，下单时写给订单，之后查单/发起支付都用它当凭据。
 *     每次「开始点餐」都重新生成一个，**不跨顾客复用**。
 *     如果整台机器共用一个固定值，那么 A 顾客完全可能用订单号 + 这个公共凭据
 *     查到 B 顾客的订单（后端凭据校验就形同虚设了）。
 *     所以格式里带设备号 + 随机串：既能定位是哪台机器，又不跨顾客。
 *
 *   idempotencyKey —— 幂等键，用来防「顾客连点了两下提交」变成两张单。
 *     同一单必须用同一个键；一旦进入下一单，必须换新的，
 *     否则下一单会被后端当成重复提交，直接返回上一单。
 */
import { randomToken } from '../config';

export interface KioskSession {
  customerOpenId: string;
  /** 当前这一单的幂等键 */
  idempotencyKey: string;
  /** 开始点餐的时间，用于日志排查 */
  startedAt: number;
}

/** 新开一次顾客会话（点「开始点餐」时调） */
export function createSession(deviceId: string, now: number = Date.now()): KioskSession {
  return {
    customerOpenId: `kiosk:${deviceId}:${randomToken(8)}`,
    idempotencyKey: newIdempotencyKey(deviceId, now),
    startedAt: now,
  };
}

/** 生成一个新的幂等键 */
export function newIdempotencyKey(deviceId: string, now: number = Date.now()): string {
  return `${deviceId}-${now.toString(36)}-${randomToken(6)}`;
}

/**
 * 下单被服务端拒绝（售罄/改价）后，换一个新的幂等键。
 *
 * 为什么不复用旧的：旧键在服务端可能已经留下了一张单（例如响应丢了但其实建单成功），
 * 顾客改完菜单重新提交时，复用旧键会拿回那张旧单 —— 内容和他现在要的不一样。
 * 这是「重复提交」和「改完再交」必须区分开的地方。
 */
export function nextAttemptKey(session: KioskSession, deviceId: string, now: number = Date.now()): string {
  const key = newIdempotencyKey(deviceId, now);
  session.idempotencyKey = key;
  return key;
}
